import {
  BehaviorSubject,
  catchError,
  debounceTime,
  combineLatest,
  EMPTY,
  distinctUntilChanged,
  filter,
  map,
  merge,
  shareReplay,
  switchMap,
  tap,
  startWith,
} from "rxjs";
import { odataList } from "./odataApi";
import { ApiSchema } from "@api-types";
import { getClientService } from "./client.service";
import { getWebsocketService } from "./websocket.service";
import { initService } from "helpers/initService";
import { getUserService } from "./user.service";

// Long enough to absorb a focus-plus-push burst, short enough to feel instant.
const REFRESH_COALESCE_MS = 250;

type LocalFlags = "beta-enabled";
export type FeatureFlag = Pick<ApiSchema.FeatureFlagView, "id" | "ff">;
class FFService {
  private readonly _ffs$ = new BehaviorSubject<FeatureFlag[] | null>(null);
  public readonly ffs$ = this._ffs$.asObservable();
  private readonly localFlags$ = new BehaviorSubject<LocalFlags[]>(
    localStorage?.getItem && localStorage.getItem("beta-enabled") ? ["beta-enabled"] : [],
  );
  constructor() {
    const client$ = getClientService().client$.pipe(
      filter((r) => !!r),
      shareReplay(1),
    );

    // The push is merged in at the TOP level rather than nested inside the settle gate.
    // Nested, it only stays alive while the gate's inner subscription does, and the initial
    // fetch still runs via the gate, so a dead push path looks identical to a working one.
    const badge = (message: string) => () =>
      console.info(
        `%c\u2691 feature flags%c ${message}`,
        "background:#4f46e5;color:#fff;padding:2px 6px;border-radius:3px 0 0 3px;font-weight:600",
        "background:#eeecfb;color:#4f46e5;padding:2px 6px;border-radius:0 3px 3px 0",
      );
    combineLatest([
      getUserService().user$.pipe(filter((u) => !!u)),
      client$,
      merge(
        getWebsocketService().featureFlagsUpdatedEvent$.pipe(tap(badge("changed, refetching"))),
        // Events fired while the socket was down are never replayed.
        getWebsocketService().reconnected$.pipe(tap(badge("socket reconnected, refetching"))),
      ).pipe(debounceTime(REFRESH_COALESCE_MS), startWith(null)), // this is to refresh :Shrug:
    ])
      .pipe(
        switchMap(([user, client]) =>
          odataList("getFeatureFlagMapped")
            .select("id", "ff")
            .filterOr(
              ["clientId", "eq", client.teamId ?? null],
              ["clientId", "eq", client.orgId ?? null],
              ["clientId", "eq", client.enterpriseId ?? null],
            )
            .filter("userId", "eq", user?.id ?? null)
            .filter("isEnabled", "eq", true)
            .$(),
        ),
        catchError((error) => {
          console.error("Feature flag refetch failed, keeping the last known flags.", error);
          return EMPTY;
        }),
        map((ffs) =>
          [...(Array.isArray(ffs.value) && ffs.value.length ? ffs.value : [])].filter(
            (value, index, array) => array.indexOf(value) === index,
          ),
        ),
      )
      .subscribe((ffs) => this._ffs$.next(ffs));
  }

  public featureEnabled = (ff: ApiSchema.FF) => !!this._ffs$.value?.find((f) => f.ff === ff);
  public featureEnabled$ = (ff: ApiSchema.FF) =>
    this.ffs$.pipe(
      filter((f) => !!f),
      map((f) => !!f?.find((f) => f.ff === ff)),
      distinctUntilChanged(),
      shareReplay(),
    );

  public toggleLocalFlag = (f: LocalFlags) => {
    if (this.localFlags$.value.includes(f)) {
      localStorage.removeItem(f);
      this.localFlags$.next([]);
    } else {
      localStorage.setItem(f, "true");
      this.localFlags$.next([f]);
    }
  };
  public localFlagEnabled$ = (f: LocalFlags) =>
    this.localFlags$.pipe(
      filter((f) => !!f),
      map((z) => !!z?.find((x) => x === f)),
      distinctUntilChanged(),
      shareReplay(),
    );
}

let ffService: Readonly<FFService> | null = null;

export const getFFService = () => {
  if (!ffService) {
    ffService = initService(() => new FFService());
  }
  return ffService;
};

export default FFService;
